import React, { useEffect, useState } from 'react';
import { Form, Stack, Col, Grid, Row, IconButton, Divider, Input, InputGroup, Checkbox, Schema, FormInstance, SelectPicker} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ImageUploader from '@/components/ImageUploader';
import Textarea from '@/components/Textarea';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { EventTypeList, ScrapedEvent, ScrapSourceList, UpdateScrapedEvent } from '@/models/scraped-event.model';
import { utc_iana_time_zone_id } from '@/constants/time';
import { Icon } from '@rsuite/icons';
import { MdContentCopy, MdLink } from 'react-icons/md';
import FetchingSelect from '@/components/FetchingSelect';
import { getCountriesLookup } from '@/services/country.service';
import { getCitiesLookup, getCity } from '@/services/city.service';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { updateScrapedEvent } from '@/services/scraped-event.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import { AxiosError } from 'axios';
import EventLinksEditor from '@/components/EventLinksEditor';
import FormControl from 'rsuite/esm/FormControl';
import PageNextIcon from '@rsuite/icons/PageNext';
import ErrorMessage from '@/components/ErrorMessage';
import MapToSelectPath from '@/components/MapToSelectPath';
import { City } from '@/models/city.model';

const schema = Schema.Model<UpdateScrapedEvent>({
  id: Schema.Types.NumberType(),
  countryId: Schema.Types.NumberType(),
  cityId: Schema.Types.NumberType(),
  eventType: Schema.Types.NumberType(),
});

const emptyModel: UpdateScrapedEvent = {
  id: undefined,
  countryId: undefined,
  cityId: undefined,
  eventType: undefined,
};

interface ScrapedEventFormProps{
  formObject?: ScrapedEvent;
  fetchData: () => void;
};

const ScrapedEventForm: React.FC<ScrapedEventFormProps> = ({formObject, fetchData}) => {
  
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formValue, setFormValue] = useState<UpdateScrapedEvent>(emptyModel);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [selectedCity, setSelectedCity] = useState< City | undefined>(undefined);

  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(formObject){
      setFormValue({
        id: formObject.id,
        cityId: formObject.cityId,
        countryId: formObject.countryId,
        eventType: formObject.eventType,
      });
    }
    if(formObject?.cityId){
      updateSelectedCity(formObject.cityId);
    }
  }, [formObject]);

  const updateSelectedCity = async(cityId: number|undefined) => {
    if(!cityId){
      setSelectedCity(undefined);
      return;
    }

    const city = (await getCity(cityId)).result;
    setSelectedCity(city);
    setFormValue(pre => ({...pre, countryId: city?.country?.id}));
  };
  
  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await updateScrapedEvent(formValue);
      if (result.success) {
        toastSuccessMessage(`Scraped Event Updated Successfully`);
        fetchData();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };
  const handleCountryChange = (countryId: number) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      countryId: countryId,
      cityId: undefined,
    }));
  };
  
  return (
    <PageContent>
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:12}} style={{textAlign: 'center'}}>
              <Row >
                <Col span={{xs:24}}>
                  <label>Image</label>              
                  <ImageUploader
                    height="400px"
                    url={formObject?.imageUrl}
                    objectStorageCategory={ObjectStorageFileCategory.ScrapedEvent}
                    downloadable
                    clearable={false}
                    uploadable = {false}
                  />            
                </Col>
                <Col span={{xs:24}}>
                  {formObject?.path && formObject.path.length > 0 ? <MapToSelectPath
                    value={formObject?.path}
                    height={`400px`}
                    zoom={13}
                    disabled
                  /> : <h2>No Path Available</h2>}
                </Col>
              </Row>
            </Col> 
            <Col span={{xs:24, xl:12}} >
              <Row>
                <Col span={{xs:24, xl:18}}>
                    <label>Name </label>
                    <Input value={formObject?.name} readOnly/> 
                </Col>             
                <Col span={{xs:24, xl:6}} >
                    <label>Source Id</label>
                    <Input value={formObject?.sourceId} readOnly/> 
                </Col>                
              </Row>
              <Row>
                <Col span={{xs:24}}>
                    <label>Description</label>
                    <Textarea value={formObject?.description} rows={7} readOnly/> 
                </Col>
              </Row>              
              <Row>
                <Col span={{xs:24, xl:12}}>
                    <label>Country Name</label>
                    <Input value={formObject?.countryName} readOnly/> 
                </Col>
                <Col span={{xs:24, xl:12}} >
                    <label>City Name</label>
                    <Input value={formObject?.cityName} readOnly/> 
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, xl:12}} >
                    <label>Registration Date</label>
                    <ZonedDatePicker block
                      value={formObject?.registrationDate ?? null}
                      zoneIanaId={selectedCity?.ianaTimeZoneId ?? utc_iana_time_zone_id}
                      readOnly
                    />                  
                </Col>
                <Col span={{xs:24, xl:12}} >
                    <label>Start Date</label>
                    <ZonedDatePicker block
                      value={formObject?.startDate ?? null}
                      zoneIanaId={selectedCity?.ianaTimeZoneId ?? utc_iana_time_zone_id}
                      readOnly
                    />                  
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <label>Website Url</label>
                  <InputGroup inside>
                    <InputGroup.Button onClick={() => formObject?.sourceWebsiteUrl && window.open(formObject?.sourceWebsiteUrl, '_blank') }>
                      <Icon as={MdLink} />
                    </InputGroup.Button>
                    <Input value={formObject?.sourceWebsiteUrl} readOnly/> 
                    <InputGroup.Button onClick={() => navigator.clipboard.writeText(formObject?.sourceWebsiteUrl || '')}>
                      <Icon as={MdContentCopy} />
                    </InputGroup.Button>
                  </InputGroup>                    
                </Col>
              </Row>  
              <Row>
                <Col span={{xs:24}}>
                  <label>Links</label>
                  <EventLinksEditor value={formObject?.links} readOnly />
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, xl:6}} >
                    <label>Source</label>
                    <Input value={ScrapSourceList.findLast(s => s.value == formObject?.source)?.label ?? ''} readOnly/> 
                </Col>
                <Col span={{xs:24, xl:6}}>
                    <label>Is Approved</label><br />
                    <Checkbox checked={!!formObject?.isApproved} readOnly/>                      
                </Col>
              </Row>  

              <Divider />
              
              <Form name='scraped-event-form' fluid 
                ref={formRef}
                onChange={handleFormChange}
                formValue={formValue}
                model={schema}
                disabled={loading || formObject?.isApproved}
              >
                  <Row>
                    <Col span={{xs:24, xl:8}} >
                        <Form.Group>
                          <Form.Label>Country</Form.Label>
                          <FormControl accepter={FetchingSelect} block
                            name='countryId'
                            fetchData={(text, page) => getCountriesLookup(text, page)} 
                            placeholder="Select a country"
                            onChange={handleCountryChange}
                          />
                        </Form.Group>
                    </Col>                
                    <Col span={{xs:24, xl:8}} >
                        <Form.Group>
                          <Form.Label>City</Form.Label>
                          <FormControl accepter={FetchingSelect} block
                            name='cityId'
                            fetchData={(text, page) => getCitiesLookup(text, formValue.countryId, page)} 
                            placeholder="Select a city"
                            reFetchOnOpen
                            onChange={cityId => updateSelectedCity(cityId)}
                          />
                        </Form.Group>
                    </Col>                
                    <Col span={{xs:24, xl:8}} >
                        <Form.Group>
                          <Form.Label>Event Type</Form.Label>
                          <Form.Control name="eventType" accepter={SelectPicker} block
                            data={EventTypeList} 
                            searchable={false}
                            placeholder="Select an event type"
                          /> 
                        </Form.Group>
                    </Col>             
                  </Row>
              </Form>
            </Col>            
          </Row>          
        </Grid>

          
          <Divider />
          {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

          <Stack justifyContent="space-between">
            <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
              Back
            </IconButton>
            <IconButton
              icon={<PageNextIcon />}
              placement="right"
              appearance="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={loading || formObject?.isApproved}
            >
              Save
            </IconButton>            
          </Stack>
    </PageContent>
  );
};

export default ScrapedEventForm;
